import { ref, onBeforeUnmount } from 'vue'
import { useMainStore } from '@/store/Pinia/useMain.store'
import { useToastGlobal } from '@/store/Pinia/useToastGlobal.store'
import { server } from '@/infra/server'

const ALLOWED_URL_PATTERN =
  /^https:\/\/certifierauthhub\.(dev\.|hom\.)?vaultid\.com\.br/

const POPUP_CONFIG = {
  width: 640,
  height: 768,
  name: 'HubAuthPopup',
}

const OTP_MAX_LENGTH = 128

export interface UseHubAuthPopupOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onPopupClosed?: () => void
}

export function useHubAuthPopup(options: UseHubAuthPopupOptions = {}) {
  const mainStore = useMainStore()
  const toastStore = useToastGlobal()

  const popupRef = ref<Window | null>(null)
  const isPopupOpen = ref(false)
  const isLoading = ref(false)
  const isCleanedUp = ref(false)
  const pollIntervalId = ref<ReturnType<typeof setInterval> | null>(null)

  const handleMessage = (event: MessageEvent) => {
    if (!ALLOWED_URL_PATTERN.test(event.origin)) {
      return
    }

    if (
      event.data &&
      event.data.type === 'signature_session_otp' &&
      (typeof event.data.otp === 'string' || typeof event.data.otp === 'number')
    ) {
      const otp = String(event.data.otp)
      console.warn('Received OTP from Hub Auth Popup:', otp)

      if (otp.length === 0 || otp.length > OTP_MAX_LENGTH) {
        console.warn('Invalid OTP length received:', otp.length)
        return
      }

      mainStore.setBirdSessionToken(otp)

      if (
        typeof event.data.cpf_cnpj === 'string' &&
        event.data.cpf_cnpj.trim().length > 0
      ) {
        mainStore.setHubCpfCnpj(event.data.cpf_cnpj.trim())
      }

      closePopup()
      options.onSuccess?.()
    }
  }

  const startPopupClosedPolling = () => {
    stopPopupClosedPolling()

    pollIntervalId.value = setInterval(() => {
      if (isCleanedUp.value) return

      if (popupRef.value && popupRef.value.closed) {
        stopPopupClosedPolling()
        isPopupOpen.value = false
        popupRef.value = null
        options.onPopupClosed?.()
      }
    }, 500)
  }

  const stopPopupClosedPolling = () => {
    if (pollIntervalId.value) {
      clearInterval(pollIntervalId.value)
      pollIntervalId.value = null
    }
  }

  const getPopupFeatures = (): string => {
    const { width, height } = POPUP_CONFIG
    const left = Math.max(0, (window.screen.width - width) / 2)
    const top = Math.max(0, (window.screen.height - height) / 2)

    return [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      'scrollbars=yes',
      'resizable=yes',
      'status=yes',
      'toolbar=no',
      'menubar=no',
      'location=no',
    ].join(',')
  }

  const openPopup = async (): Promise<boolean> => {
    if (isLoading.value) {
      return false
    }

    if (isPopupOpen.value && popupRef.value && !popupRef.value.closed) {
      popupRef.value.focus()
      return true
    }

    if (!mainStore.enviroment) {
      mainStore.fetchEnviroment()
    }

    isLoading.value = true

    try {
      const response = await server.get('/hub-auth-url')
      const hubUrl = response.data?.url || response.data

      if (
        typeof hubUrl !== 'string' ||
        !hubUrl ||
        !ALLOWED_URL_PATTERN.test(hubUrl)
      ) {
        throw new Error('URL de autenticação inválida')
      }

      window.addEventListener('message', handleMessage)

      popupRef.value = window.open(
        hubUrl,
        POPUP_CONFIG.name,
        getPopupFeatures(),
      )

      if (!popupRef.value || popupRef.value.closed) {
        window.removeEventListener('message', handleMessage)
        toastStore.toggleToastGlobal({
          isShow: true,
          type: 'warning',
          message:
            'Popup bloqueado pelo navegador. Por favor, permita popups para este site e tente novamente.',
        })
        options.onError?.(new Error('Popup blocked by browser'))
        return false
      }

      isPopupOpen.value = true
      startPopupClosedPolling()
      popupRef.value.focus()

      return true
    } catch (error) {
      window.removeEventListener('message', handleMessage)
      toastStore.toggleToastGlobal({
        isShow: true,
        type: 'danger',
        message: 'Erro ao abrir autenticação. Tente novamente.',
      })
      options.onError?.(
        error instanceof Error ? error : new Error(String(error)),
      )
      return false
    } finally {
      isLoading.value = false
    }
  }

  const closePopup = () => {
    stopPopupClosedPolling()
    window.removeEventListener('message', handleMessage)

    if (popupRef.value && !popupRef.value.closed) {
      popupRef.value.close()
    }

    popupRef.value = null
    isPopupOpen.value = false
  }

  onBeforeUnmount(() => {
    isCleanedUp.value = true
    closePopup()
  })

  return {
    isPopupOpen,
    isLoading,
    openPopup,
    closePopup,
  }
}
